import { characterLabel } from "./shopImages";

const MAX_BYTES = 10 * 1024 * 1024;

export const ACCEPTED_TYPES = Object.freeze([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

export const fileProblem = ({ type, size }) =>
  !ACCEPTED_TYPES.includes(type)
    ? { code: "unsupported-type", message: "Use a JPEG, PNG or WebP image." }
    : size > MAX_BYTES
    ? { code: "too-large", message: "Use an image under 10 MB." }
    : null;

export { centredSquare } from "../../shared/shopImages/geometry.js";
export { isLowResolution } from "../../shared/shopImages/resolution.js";

export const QUEUE = Object.freeze({
  FILES_QUEUED: "files-queued",
  CROPPED: "cropped",
  DETAILS_CHANGED: "details-changed",
  UPLOAD_STARTED: "upload-started",
  UPLOAD_SUCCEEDED: "upload-succeeded",
  UPLOAD_FAILED: "upload-failed",
  RETRIED: "retried",
  DISCARDED: "discarded",
  CLEARED_DONE: "cleared-done",
});

export const initialQueue = Object.freeze({ items: [] });

const PORTRAIT = 3 / 4;

export const defaultCrop = ({ width, height }) => {
  const isWide = width / height > PORTRAIT;
  const cropWidth = isWide ? Math.floor(height * PORTRAIT) : width;
  const cropHeight = isWide ? height : Math.floor(width / PORTRAIT);

  return {
    x: Math.floor((width - cropWidth) / 2),
    y: Math.floor((height - cropHeight) / 2),
    width: cropWidth,
    height: cropHeight,
  };
};

const queuedItem = (kind) => (file) => ({
  label: null,
  influencerCharacterId: null,
  ...file,
  kind,
  crop: kind === "banner" ? null : file.crop ?? defaultCrop(file.imageSize),
  status: "ready",
  error: null,
  warnings: [],
});

const updateItem = (state, id, changes) => ({
  ...state,
  items: state.items.map((item) =>
    item.id === id ? { ...item, ...changes } : item,
  ),
});

const HANDLERS = Object.freeze({
  [QUEUE.FILES_QUEUED]: (state, { kind, files }) => ({
    ...state,
    items: [...state.items, ...files.map(queuedItem(kind))],
  }),
  [QUEUE.CROPPED]: (state, { id, crop }) => updateItem(state, id, { crop }),
  [QUEUE.DETAILS_CHANGED]: (state, { id, details }) =>
    updateItem(state, id, details),
  [QUEUE.UPLOAD_STARTED]: (state, { id }) =>
    updateItem(state, id, { status: "uploading", error: null }),
  [QUEUE.UPLOAD_SUCCEEDED]: (state, { id, warnings }) =>
    updateItem(state, id, { status: "done", warnings }),
  [QUEUE.UPLOAD_FAILED]: (state, { id, message }) =>
    updateItem(state, id, { status: "failed", error: message }),
  [QUEUE.RETRIED]: (state, { id }) =>
    updateItem(state, id, { status: "ready", error: null }),
  [QUEUE.DISCARDED]: (state, { id }) => ({
    items: state.items.filter((item) => item.id !== id),
  }),
  [QUEUE.CLEARED_DONE]: (state) => ({
    items: state.items.filter((item) => item.status !== "done"),
  }),
});

export const queueReducer = (state, action) =>
  HANDLERS[action.type]?.(state, action) ?? state;

export const nextToUpload = ({ items }) =>
  items.find((item) => item.status === "ready") ?? null;

export const moveImage = (imageIds, imageId, delta) => {
  const from = imageIds.indexOf(imageId);
  const to = Math.min(Math.max(from + delta, 0), imageIds.length - 1);
  const without = imageIds.filter((id) => id !== imageId);

  return [...without.slice(0, to), imageId, ...without.slice(to)];
};

export const toFront = (imageIds, imageId) =>
  moveImage(imageIds, imageId, -imageIds.length);

const nameOf = (image) => characterLabel(image) ?? "Image";

export const movedAnnouncement = (image, position) =>
  position === 0
    ? `${nameOf(image)} moved to position 1. It is now the primary image.`
    : `${nameOf(image)} moved to position ${position + 1}.`;

const images = (count) => (count === 1 ? "1 image" : `${count} images`);

export const uploadAnnouncement = ({ done, failed }) =>
  done === 0
    ? `${images(failed)} failed to upload.`
    : failed === 0
    ? `${images(done)} uploaded.`
    : `${images(done)} uploaded. ${failed} failed.`;

export const removalNote = (characters, imageId) => {
  const removed = characters.find((image) => image.imageId === imageId);
  const successor = characters.find((image) => image.position === 1);

  return removed?.position !== 0
    ? null
    : successor
    ? `${nameOf(successor)} will become the primary image.`
    : "Cards will show the headshot instead.";
};

const offsetPercent = (start, span, total) =>
  span >= total ? 0 : (start / (total - span)) * 100;

export const previewBackground = (area, imageSize) => ({
  backgroundSize: `${(imageSize.width / area.width) * 100}% ${
    (imageSize.height / area.height) * 100
  }%`,
  backgroundPosition: `${offsetPercent(
    area.x,
    area.width,
    imageSize.width,
  )}% ${offsetPercent(area.y, area.height, imageSize.height)}%`,
});
